import React, {useState} from 'react';
import { Button, Card, Empty } from 'antd';
import { LineChartOutlined, BarChartOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './SalesComparisonChart.scss';

const SalesComparisonChart = ({ 
  title, 
  currentWeekData = [], 
  previousWeekData = [], 
  loading = false 
}) => {
  // Combine the data for the chart
  const combinedData = [];
  const maxLength = Math.max(currentWeekData.length, previousWeekData.length);
  const [isComulativeView, setIsComulativeView] = useState(false);
  
  for (let i = 0; i < maxLength; i++) {
    const currentItem = currentWeekData[i];
    const previousItem = previousWeekData[i];
    
    const dataPoint = {
      date: currentItem?.date || previousItem?.date || `Dia ${i + 1}`,
      semanaAtual: currentItem ? (currentItem.total / 100) : 0,
      semanaAnterior: previousItem ? (previousItem.total / 100) : 0,
    };
    
    combinedData.push(dataPoint);
  }

  const formatTooltipValue = (value, name) => {
    return [
      `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      name
    ];
  };

  const formatAxisLabel = (value) => {
    if (value.includes('-')) {
      // Format date from YYYY-MM-DD to DD/MM
      const [, month, day] = value.split('-');
      return `${day}/${month}`;
    }
    return value;
  };
  // Check if we have any data
  const hasData = currentWeekData.length > 0 || previousWeekData.length > 0;

  const getData = (data) => {
    if (isComulativeView) {
      return data.map((item, index) => ({
        ...item,
        semanaAtual: data.slice(0, index + 1).reduce((acc, curr) => acc + curr.semanaAtual, 0),
        semanaAnterior: data.slice(0, index + 1).reduce((acc, curr) => acc + curr.semanaAnterior, 0),
      }));
    }
    return data;
  };

  return (
    <Card 
      className="sales-comparison-chart" 
      title={title}
      loading={loading}
      extra={
        hasData && (
          <Button 
            size="small"
            icon={isComulativeView ? <LineChartOutlined /> : <BarChartOutlined />}
            onClick={() => setIsComulativeView(!isComulativeView)}
          >
            {isComulativeView ? 'Cumulativo' : 'Diário'}
          </Button>
        )
      }
    >
      {!hasData ? (
        <div className="empty-state">
          <Empty 
            description="Não há dados disponíveis"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={getData(combinedData)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatAxisLabel}
            />
            <YAxis 
              tickFormatter={(value) => 
                `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
              }
            />
            <Tooltip 
              formatter={formatTooltipValue}
              labelFormatter={(label) => `Data: ${formatAxisLabel(label)}`}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="semanaAtual" 
              stroke="#1890ff" 
              strokeWidth={2}
              name="Período Atual"
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="semanaAnterior" 
              stroke="#52c41a" 
              strokeWidth={2}
              name="Período Anterior"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default SalesComparisonChart;
